import type { LocalDate } from "@freightsimple/time/branded-types";
import {
  addMonths,
  daysUntil,
  lastOfMonth,
  today,
} from "@freightsimple/time/helpers";

// A credit card is valid through the LAST day of its expiry month (industry
// convention, matches Stripe). It is considered expired from the first day of
// the following month. "Today" is evaluated in Vancouver time, consistent with
// the rest of the platform — the backend applies the same definition in
// PaymentMethodsController. (ENG-4368)

/**
 * Parses a card expiry string into the last calendar day the card is valid.
 *
 * The backend sends `"M/YYYY"` (from Stripe's expMonth/expYear), but some
 * legacy data may carry two-digit years, so `"M/YY"` is tolerated.
 * Returns undefined when the string is unparseable.
 */
export function lastValidDayForCreditCardExpiry(
  expiry: string,
): LocalDate | undefined {
  const [monthPart, yearPart] = expiry.split("/");
  const month = Number(monthPart);
  let year = Number(yearPart);
  if (!Number.isInteger(month) || month < 1 || month > 12) {
    return undefined;
  }
  if (!Number.isInteger(year) || year < 0) {
    return undefined;
  }
  if (year < 100) {
    year += 2000;
  }
  return lastOfMonth(year, month - 1);
}

/**
 * True when the card's expiry month is fully in the past.
 * Unparseable expiry strings are treated as not expired — the backend guard
 * is the authority and will reject anything genuinely invalid.
 */
export function isCreditCardExpired(expiry: string): boolean {
  const lastValidDay = lastValidDayForCreditCardExpiry(expiry);
  if (lastValidDay === undefined) {
    return false;
  }
  return daysUntil(lastValidDay) < 0;
}

/**
 * True when the card expires within the next `withinMonths` months (default 3)
 * but has not expired yet. Used for "expiring soon" warnings.
 */
export function isCreditCardExpiringSoon(
  expiry: string,
  withinMonths: number = 3,
): boolean {
  const lastValidDay = lastValidDayForCreditCardExpiry(expiry);
  if (lastValidDay === undefined) {
    return false;
  }
  if (daysUntil(lastValidDay) < 0) {
    return false;
  }
  const horizon = addMonths(today(), withinMonths);
  return lastValidDay <= horizon;
}
